import React, { useCallback, useMemo, useState } from 'react';
import { useTheme, makeStyles, useMediaQuery } from '@material-ui/core';
import { InfoOutlined } from '@material-ui/icons';
import { Link } from '@carbon/icons-react';
import AppConfig from '@config';
import { useSelector } from '@store';
import { documentDateFormat } from '@utils/formatter';
import {
  CapaQuotationChatRoomStatus,
  useFetchMyQuotationUserQuery,
  CapaQuotationUserVerificationType
} from '@generated/graphql';
import { colorSet, useSnackbar } from '@components/Provider';
import { CustomizedButton, CustomizedTypography } from '@components/Customized';
import QuotationStepper from './QuotationStepper';
import QuotationHistoryBar from './QuotationHistoryBar';
import QuotationConfirmModal from './QuotationConfirmModal';
import QuotationFixedBottomCTA from './QuotationFixedBottomCTA';
import QuotationRequestForFixModal from './QuotationRequestForFixModal';
import { VerificationDialog } from '@routes/MyAccount/components/Dialog/RequestActionDialog';

interface Props {
  isLatestQuotation?: boolean;
  isFirstCreatedQuotation?: boolean;
  isWithdraw?: boolean;
  isClosedConsulting: boolean;
  createdAt?: string;
  projectName?: string;
  rfqId?: string;
  partnerId?: string;
  quotationId?: string;
  children: React.ReactNode;
}

export type ModalType =
  | 'fix-request'
  | 'create-purchaseOrder'
  | 'not-verified'
  | null;

const CLOSED_CHATROOM_STATES: (
  | CapaQuotationChatRoomStatus
  | undefined
  | null
)[] = [
  CapaQuotationChatRoomStatus.ClosedConfirm,
  CapaQuotationChatRoomStatus.ClosedClient,
  CapaQuotationChatRoomStatus.ClosedPartner
];

const QuotationCommonWrapper = ({
  children,
  createdAt,
  isWithdraw,
  isLatestQuotation,
  projectName,
  quotationId,
  partnerId = '',
  rfqId = ''
}: Props) => {
  const isDashboard = window.location.pathname.includes('/dashboard');
  const isClipboard = window.location.pathname.includes('/clipboard');
  const classes = useStyles();
  const matches = useMediaQuery(useTheme().breakpoints.down('md'));
  const [modal, setModal] = useState<ModalType>(null);
  const { enqueueSnackbar } = useSnackbar();
  const chatRoomStatus = useSelector((state) => state.chat.chatRoomStatus);
  const hasProgressingPayment = useSelector(
    (state) => state.chat.hasProgressingPayment
  );
  const { data: userInfo } = useFetchMyQuotationUserQuery();
  const isNotVerifiedUser =
    userInfo?.fetchMyQuotationUser?.data?.verificationType !==
    CapaQuotationUserVerificationType.Verified;

  const isErrorBackgrond = isWithdraw;
  const buttonDisabled =
    isWithdraw || CLOSED_CHATROOM_STATES.includes(chatRoomStatus);

  const onClickClipboard = () => {
    let url = '';
    const textarea = document.createElement('textarea');
    document.body.appendChild(textarea);
    url = `${AppConfig.baseUrl}/clipboard/quotation?quotationId=${quotationId}&rfqId=${rfqId}`;
    textarea.value = url;
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);

    enqueueSnackbar('링크를 복사하였습니다.', { variant: 'success' });
  };

  const handleModal = useCallback((type: ModalType) => setModal(type), []);

  const renderHeaderText = () => {
    const isPrevious = !isWithdraw && !isLatestQuotation;

    if (isWithdraw) {
      return (
        <>
          <InfoOutlined /> 본 견적서는 탈퇴한 파트너가 발행한 견적서입니다.
        </>
      );
    }

    if (isPrevious) {
      return (
        <>
          최신견적이 아닌 {documentDateFormat(createdAt ?? '')}에 발행된
          견적입니다.
        </>
      );
    }

    return (
      <>{documentDateFormat(createdAt ?? '')}에 발행된 최신 견적서 입니다.</>
    );
  };

  const modalRenderer = useMemo(() => {
    switch (modal) {
      case 'fix-request':
        return (
          <QuotationRequestForFixModal
            matches={matches}
            open={true}
            onClose={() => handleModal(null)}
            rfqId={rfqId}
            partnerId={partnerId}
            hasProgressingPayment={hasProgressingPayment}
          />
        );

      case 'create-purchaseOrder':
        return (
          <QuotationConfirmModal
            open={true}
            onClose={() => handleModal(null)}
            handleModal={handleModal}
            rfqId={rfqId}
            partnerId={partnerId}
            quotationId={quotationId}
          />
        );

      case 'not-verified':
        return (
          <VerificationDialog open={true} onClose={() => handleModal(null)} />
        );

      default:
        return null;
    }
  }, [
    modal,
    matches,
    hasProgressingPayment,
    rfqId,
    partnerId,
    quotationId,
    handleModal
  ]);

  return (
    <>
      {modalRenderer}

      <div className={classes.root}>
        {!isDashboard && isLatestQuotation && (
          <QuotationStepper activeStep={1} />
        )}

        <div className={classes.children}>
          {!isClipboard && !isDashboard && (
            <QuotationHistoryBar
              color={
                isErrorBackgrond
                  ? 'error'
                  : isLatestQuotation
                  ? 'primary'
                  : 'default'
              }
            >
              {renderHeaderText()}
            </QuotationHistoryBar>
          )}

          <div className={classes.projectName}>
            <CustomizedTypography fontWeight="bold" variant="h3" noWrap>
              {projectName}
            </CustomizedTypography>

            <div className="inner_title">
              <CustomizedButton
                variant="text"
                color="dark"
                startIcon={<Link size={20} />}
                className="link-icon-button"
                onClick={onClickClipboard}
                style={{
                  paddingRight: 0
                }}
              >
                링크복사
              </CustomizedButton>
            </div>
          </div>
          {children}
        </div>
      </div>

      {!isClipboard && !isDashboard && isLatestQuotation && (
        <QuotationFixedBottomCTA className={classes.fixedBottom}>
          <CustomizedButton
            disabled={buttonDisabled}
            className="first"
            fullWidth
            variant="contained"
            color={hasProgressingPayment ? 'primary' : 'default'}
            size="large"
            onClick={() => handleModal('fix-request')}
          >
            견적서 수정 요청
          </CustomizedButton>

          {!hasProgressingPayment && (
            <CustomizedButton
              disabled={buttonDisabled}
              fullWidth
              variant="contained"
              color="primary"
              size="large"
              onClick={async () => {
                handleModal(
                  isNotVerifiedUser ? 'not-verified' : 'create-purchaseOrder'
                );
              }}
            >
              <i className="request-gtm-purchaseOrder-btn">이 조건으로 발주</i>
            </CustomizedButton>
          )}
        </QuotationFixedBottomCTA>
      )}
    </>
  );
};

export default QuotationCommonWrapper;

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    overflowY: 'scroll',
    '&.dashboard': {
      overflowY: 'inherit'
    }
  },
  projectName: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'relative',
    margin: `20px 0px`,
    minWidth: 0,
    '& h3': {
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    },
    '& .inner_title': {
      '& svg': {
        color: colorSet.gray900
      },
      '& .MuiIconButton-root': {
        padding: 6
      },
      '& > :first-child': {
        marginRight: 4
      }
    }
  },
  children: {
    maxWidth: 600,
    width: '100%',
    padding: '32px 24px',

    [theme.breakpoints.down('md')]: {
      maxWidth: '100%',
      padding: '20px 16px'
    },
    '& .divider': {
      height: 1,
      width: 552,
      background: colorSet.gray400,
      margin: '32px 0px',

      [theme.breakpoints.down('md')]: {
        position: 'relative',
        width: 'calc(100% + 32px)',
        height: 16,
        left: -16,
        background: colorSet.gray200
      }
    }
  },
  fixedBottom: {
    '& button': {
      maxWidth: 167,
      [theme.breakpoints.down('md')]: {
        maxWidth: 'unset'
      },
      '&.first': {
        marginRight: 12
      }
    }
  },
  withdrawal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    '& svg': {
      width: 20,
      height: 20,
      marginRight: 8,
      color: colorSet.errorMain
    }
  }
}));
